import React, { useState } from 'react'
import { colors, Link, Loading } from '@resiliatech/birdsong'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
} from '@mui/material'
import { ImpactDashboardActivity } from 'Generated/graphql-types'
import { snakeCaseToSpaces, toTitleCase } from 'Utils/formatters'
import { useAppState } from 'Context/AppContext/AppContext'
import { WithWidgetLoading } from '../WithWidgetLoading'
import {
  AcademyDigestContainer,
  AcademyTableContainer,
  StatusChip,
} from './AcademyDigest.styled'
import { ResourceIdToTaskIdMap } from '../types'
import { getSortedUserResources } from '../utils'
import { buildResourcePath } from '../../../Academy/_common/utils'
import { dispatchAcademyResourceClicked } from '../analytics.utils'

type AcademyDigestProps = {
  loading: boolean
  resourceIdToTaskIdMap: ResourceIdToTaskIdMap
  userResourceActivity: ImpactDashboardActivity[]
}

export const AcademyDigest = ({
  loading,
  resourceIdToTaskIdMap,
  userResourceActivity,
}: AcademyDigestProps) => {
  const {
    nonprofitOrgConfig: { orgId, platformPlan, funderOrgId },
    userConfig: { id, email },
  } = useAppState()
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const handleSort = (columnId: string) => {
    if (sortKey === columnId) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(columnId)
      setSortOrder('asc')
    }
  }

  const columnHeaders = [
    { sortKey: 'title', label: 'Resource Name' },
    { sortKey: 'type', label: 'Type' },
    { sortKey: 'category', label: 'Category' },
    { sortKey: 'userFullName', label: 'Team Member' },
    { sortKey: 'userStatus', label: 'Status' },
    { sortKey: 'attachedToGoal', label: 'Attached to Goal?' },
  ]

  const userActivityWithIsGoalAttached: (ImpactDashboardActivity & {
    isAttachedToGoal: boolean
  })[] = userResourceActivity.map((activity) => {
    const taskId = resourceIdToTaskIdMap[activity.resource.id]
    return {
      ...activity,
      isAttachedToGoal: !!taskId,
    }
  })

  const sortedAcademyUserActivity = getSortedUserResources({
    sortKey,
    sortOrder,
    resourcesToDisplay: userActivityWithIsGoalAttached,
  })

  return (
    <WithWidgetLoading
      loading={loading}
      width='100%'
      height={loading ? '600px' : 'auto'}
    >
      <AcademyDigestContainer>
        <Paper>
          <AcademyTableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columnHeaders.map((h) => (
                    <TableCell
                      key={h.label}
                      sx={{
                        backgroundColor: colors.background.secondaryBackground,
                      }}
                    >
                      {' '}
                      <TableSortLabel
                        active={sortKey === h.sortKey}
                        direction={sortKey === h.sortKey ? sortOrder : 'asc'}
                        onClick={() => handleSort(h.sortKey)}
                        sx={{ minWidth: '100px' }}
                      >
                        {h.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAcademyUserActivity.map((row) => {
                  const resourcePath = buildResourcePath(
                    row.resource.type === 'LearningTrack'
                      ? { id: row.resource.id, type: 'learning_track' }
                      : row.resource
                  )
                  const renderAttachedToGoalStatus = () => {
                    if (loading) {
                      return <Loading />
                    }
                    return row.isAttachedToGoal ? 'Yes' : 'No'
                  }
                  return (
                    <TableRow key={`${row.userId}-${row.resource.id}`}>
                      <TableCell>
                        <Link
                          href={resourcePath}
                          target='_blank'
                          rel='noopener'
                          onClick={() => {
                            dispatchAcademyResourceClicked({
                              academyReourceTitle: row.resource.title,
                              academyResourceType: row.resource.type,
                              academyResourceCategory:
                                row.resource.nonprofitTopic,
                              academyResourceId: row.resource.id,
                              funderOrgId: funderOrgId || null,
                              userId: id,
                              userEmail: email,
                              orgId,
                              platformPlan: platformPlan || '',
                            })
                          }}
                        >
                          {row.resource.title}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {toTitleCase(snakeCaseToSpaces(row.resource.type))}
                      </TableCell>
                      <TableCell>{row.resource.nonprofitTopic}</TableCell>
                      <TableCell>{row.userFullName}</TableCell>
                      <TableCell>
                        <StatusChip
                          label={toTitleCase(snakeCaseToSpaces(row.userStatus))}
                        />
                      </TableCell>
                      <TableCell>{renderAttachedToGoalStatus()}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </AcademyTableContainer>
        </Paper>
      </AcademyDigestContainer>
    </WithWidgetLoading>
  )
}
